import { c as _c } from "react/compiler-runtime";
import { useState, useRef } from "react";
import NextLink from "next/link";
import { Chip } from "@/components/ui/Chip";
import type { DynamicFormData, DynamicFormMessage as DynamicFormMessageType } from "@/types/agent/message";
import { SpeechBubble } from "./SpeechBubble";
import { getTranslations, type Locale } from "@/lib/i18n/config";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const getFileKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;
interface DynamicFormMessageProps {
  message: DynamicFormMessageType;
  locale?: Locale;
}
export const DynamicFormMessage: React.FC<DynamicFormMessageProps> = t0 => {
  const $ = _c(24);
  const {
    message,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "de" : t1;
  const {
    agent: t
  } = getTranslations(locale);
  let t2;
  if ($[0] !== message.fields || $[1] !== message.submittedData) {
    t2 = () => Object.fromEntries(message.fields.map(field => [field.name, message.submittedData?.[field.name] ?? field.value ?? ""]));
    $[0] = message.fields;
    $[1] = message.submittedData;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [formData, setFormData] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [fileData, setFileData] = useState(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [fileNames, setFileNames] = useState(t4);
  const [fileError, setFileError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(message.isSubmitted);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const fileInputRefs = useRef(t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (name, value) => {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleChange = t6;
  const handleFileChange = async (name_0, files) => {
    setFileError(null);
    if (!files || files.length === 0) {
      setFormData(prev_0 => ({
        ...prev_0,
        [name_0]: ""
      }));
      setFileData(prev_1 => ({
        ...prev_1,
        [name_0]: []
      }));
      setFileNames(prev_2 => ({
        ...prev_2,
        [name_0]: []
      }));
      return;
    }
    const validFiles = [];
    for (const file of Array.from(files)) {
      if (file.type !== "application/pdf") {
        setFileError(t.form.pdfOnly);
        if (fileInputRefs.current[name_0]) {
          fileInputRefs.current[name_0].value = "";
        }
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setFileError(t.form.fileTooLarge.replace("{name}", file.name));
        if (fileInputRefs.current[name_0]) {
          fileInputRefs.current[name_0].value = "";
        }
        return;
      }
      validFiles.push(file);
    }
    const existingFiles = fileData[name_0] ?? [];
    const existingFileKeys = new Set(existingFiles.map(getFileKey));
    const uniqueNewFiles = validFiles.filter(file_0 => {
      const key = getFileKey(file_0);
      if (existingFileKeys.has(key)) {
        return false;
      }
      existingFileKeys.add(key);
      return true;
    });
    const nextFiles = [...existingFiles, ...uniqueNewFiles];
    const names = nextFiles.map(_temp);
    setFileData(prev_3 => ({
      ...prev_3,
      [name_0]: nextFiles
    }));
    setFormData(prev_4 => ({
      ...prev_4,
      [name_0]: names.join(", ")
    }));
    setFileNames(prev_5 => ({
      ...prev_5,
      [name_0]: names
    }));
    if (fileInputRefs.current[name_0]) {
      fileInputRefs.current[name_0].value = "";
    }
  };
  let t7;
  if ($[7] !== fileData) {
    t7 = (fieldName, index) => {
      const updatedFiles = (fileData[fieldName] ?? []).filter((_, i) => i !== index);
      const updatedNames = updatedFiles.map(_temp2);
      setFileData(prev_6 => ({
        ...prev_6,
        [fieldName]: updatedFiles
      }));
      setFileNames(prev_7 => ({
        ...prev_7,
        [fieldName]: updatedNames
      }));
      setFormData(prev_8 => ({
        ...prev_8,
        [fieldName]: updatedNames.join(", ")
      }));
    };
    $[7] = fileData;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleRemoveFile = t7;
  const handleSubmit = e => {
    e.preventDefault();
    const missingRequiredPdfField = message.fields.find(field_0 => field_0.type === "pdf" && field_0.required && (fileData[field_0.name]?.length ?? 0) === 0);
    if (missingRequiredPdfField) {
      setFileError(t.form.missingRequiredFile.replace("{label}", missingRequiredPdfField.label));
      return;
    }
    setFileError(null);
    const submittedData = message.fields.reduce((acc, field_1) => {
      acc[field_1.name] = field_1.type === "pdf" ? fileNames[field_1.name]?.join(", ") ?? formData[field_1.name] ?? "" : formData[field_1.name] ?? "";
      return acc;
    }, {});
    const submissionData = {
      ...formData
    };
    Object.entries(fileData).forEach(t8 => {
      const [name_1, files_0] = t8;
      if (files_0 && files_0.length > 0) {
        submissionData[name_1] = files_0.length === 1 ? files_0[0] : files_0;
      }
    });
    setIsSubmitted(true);
    message.update?.({
      isSubmitted: true,
      submittedData
    } as Partial<DynamicFormMessageType>);
    message.onSubmit?.(message.formId, submissionData);
  };
  if (isSubmitted) {
    let t9;
    if ($[9] !== fileNames || $[10] !== formData || $[11] !== message.fields) {
      let t10;
      if ($[13] !== fileNames || $[14] !== formData) {
        t10 = field_2 => {
          const value_0 = field_2.type === "pdf" ? fileNames[field_2.name]?.join(", ") : formData[field_2.name];
          if (!value_0) {
            return null;
          }
          return <div key={field_2.name} className="flex flex-col"><span className="text-xs text-black/50">{field_2.label}</span><span className="text-sm">{value_0}</span></div>;
        };
        $[13] = fileNames;
        $[14] = formData;
        $[15] = t10;
      } else {
        t10 = $[15];
      }
      t9 = message.fields.map(t10);
      $[9] = fileNames;
      $[10] = formData;
      $[11] = message.fields;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    return <SpeechBubble sender="assistant" className="flex flex-col gap-2 min-w-[280px]"><p className="text-sm font-bold">✓ {message.title} – {t.form.submitted}</p>{t9}</SpeechBubble>;
  }
  let t9;
  if ($[16] !== message.title) {
    t9 = <p className="font-bold text-base">{message.title}</p>;
    $[16] = message.title;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== message.description) {
    t10 = message.description && <p className="text-sm text-black/70">{message.description}</p>;
    $[18] = message.description;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== fileError) {
    t11 = fileError && <p className="text-xs text-red-500" role="alert" aria-live="assertive">{fileError}</p>;
    $[20] = fileError;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== message.submitText) {
    t12 = <button type="submit" className="mt-1 inline-block px-4 py-2 bg-yellow text-black rounded-full font-bold hover:bg-yellow/80 transition-colors self-start cursor-pointer">{message.submitText}</button>;
    $[22] = message.submitText;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return <SpeechBubble sender="assistant" className="min-w-[280px]"><form onSubmit={handleSubmit} className="flex flex-col gap-4" aria-label={message.title}>{t9}{t10}{message.fields.map(field_3 => {
        if (field_3.type === "pdf") {
          return <div key={field_3.name} className="flex flex-col gap-1"><span className="text-sm font-medium">{field_3.label}{field_3.required && <span className="text-red-500"> *</span>}</span><input ref={el => {
              fileInputRefs.current[field_3.name] = el;
            }} id={`pdf-upload-${field_3.name}`} type="file" name={field_3.name} accept="application/pdf" multiple={true} onChange={e_0 => handleFileChange(field_3.name, e_0.target.files)} className="hidden" /><button type="button" onClick={() => fileInputRefs.current[field_3.name]?.click()} aria-controls={`pdf-upload-${field_3.name}`} className="rounded-xl px-3 py-2 text-sm bg-white outline-1 outline-black/10 hover:outline-black/20 focus:outline-purple transition-colors text-left cursor-pointer">{t.form.selectDocuments}</button>{fileNames[field_3.name]?.length > 0 && <div className="mt-1 flex flex-wrap gap-1" aria-live="polite">{fileNames[field_3.name].map((name_2, index_0) => <Chip key={`${name_2}-${index_0}`} color="black" icon="close" iconAriaLabel={t.form.removeFile} onIconClick={() => handleRemoveFile(field_3.name, index_0)}>{name_2}</Chip>)}</div>}</div>;
        }
        return <label key={field_3.name} className="flex flex-col gap-1"><span className="text-sm font-medium">{field_3.label}{field_3.required && <span className="text-red-500"> *</span>}</span>{field_3.type === "textarea" ? <textarea name={field_3.name} required={field_3.required} value={formData[field_3.name] ?? ""} onChange={e_1 => handleChange(field_3.name, e_1.target.value)} rows={4} className="rounded-xl px-3 py-2 text-sm bg-white outline-1 outline-black/10 focus:outline-purple transition-colors resize-y" /> : <input type={field_3.type} name={field_3.name} required={field_3.required} value={formData[field_3.name] ?? ""} onChange={e_2 => handleChange(field_3.name, e_2.target.value)} className="rounded-xl px-3 py-2 text-sm bg-white outline-1 outline-black/10 focus:outline-purple transition-colors" />}</label>;
      })}{t11}<p className="text-xs text-black/50">{t.form.privacyNotice.split("{privacy-link}")[0]}<NextLink href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline hover:text-black/80 transition-colors">{t.form.privacyLinkLabel}</NextLink>{t.form.privacyNotice.split("{privacy-link}")[1]}</p>{t12}</form></SpeechBubble>;
};
function _temp(f) {
  return f.name;
}
function _temp2(file_1) {
  return file_1.name;
}